import { useMemo, useState, useRef, useEffect } from "react";
import { useNavigate, useLocation, Link } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { HiOutlineChatBubbleOvalLeft, HiOutlineEllipsisVertical } from "react-icons/hi2";
import { FaRegBookmark, FaBookmark, FaTrash, FaShare, FaLock, FaUserPlus, FaUserMinus, FaFlag, FaBan } from "react-icons/fa";
import Like from "./Like";
import { RichTextContent } from "../../utils/richTextProcessor";
import { savePostAction, unsavePostAction, getSavedPostsAction, increaseSavedByCount, decreaseSavedByCount, updatePostAction } from "../../redux/actions/postActions";
import { followUserAndFetchData, unfollowUserAction, getFollowingUsersAction, blockUserAction, unblockUserAction } from "../../redux/actions/userActions";
import * as types from "../../redux/constants/userConstants";
import DeleteModal from "../modals/DeleteModal";
import EditPostModal from "../modals/EditPostModal";
import PublishPostModal from "../modals/PublishPostModal";
import ReportPostModal from "../modals/ReportPostModal";
import { Transition } from "@headlessui/react";
import useMediaQuery from "../../hooks/useMediaQuery";

import placeholder from "../../assets/placeholder.png";
const SavedPost = ({ post }) => {
  const isSmallScreen = useMediaQuery("(max-width: 912px)");

  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch();
  const userData = useSelector((state) => state.auth?.userData);
  const savedPosts = useSelector((state) => state.posts?.savedPosts || []);
  const followingUsers = useSelector((state) => state.user?.followingUsers || []);
  const blockedUsers = useSelector((state) => state.user?.blockedUsers || []);

  const [showDropdown, setShowDropdown] = useState(false);
  const [showCommunityDropdown, setShowCommunityDropdown] = useState(false);
  const [isSaved, setIsSaved] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [followLoading, setFollowLoading] = useState(false);
  const [unfollowLoading, setUnfollowLoading] = useState(false);
  const [blockLoading, setBlockLoading] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showPublishModal, setShowPublishModal] = useState(false);
  const [showReportModal, setShowReportModal] = useState(false);
  const dropdownRef = useRef(null);
  const communityDropdownRef = useRef(null);

  // Handle invalid post data safely
  const postData = post && typeof post === 'object' ? post : {};
  const {
    content,
    fileUrl,
    fileType,
    youtubeVideo,
    user = {},
    communities = [],
    audience = "Private",
    createdAt,
    comments = [],
    allowComments = true
  } = postData;

  // Helper function to get community display info
  const getCommunityDisplay = () => {
    try {
      // Check for communities array
      if (communities &&
        Array.isArray(communities) &&
        communities.length > 0 &&
        communities[0] &&
        typeof communities[0] === 'object' &&
        communities[0]._id &&
        communities[0].name &&
        communities[0]._id !== null &&
        communities[0].name !== null) {

        if (communities.length === 1) {
          return {
            type: 'single',
            name: communities[0].name,
            id: communities[0]._id,
            allCommunities: communities
          };
        } else {
          return {
            type: 'multiple',
            name: communities[0].name,
            id: communities[0]._id,
            allCommunities: communities,
            remainingCount: communities.length - 1
          };
        }
      }

      // Check for audience types
      if (audience === "Private") {
        return {
          type: 'audience',
          name: "Private",
          id: null
        };
      }

      if (audience === "Followers") {
        return {
          type: 'audience',
          name: "Followers",
          id: null
        };
      }

      if (audience === "Anyone") {
        return {
          type: 'audience',
          name: "Anyone",
          id: null
        };
      }

      return null;
    } catch (error) {
      console.error('Error in getCommunityDisplay:', error);
      return null;
    }
  };

  const communityDisplay = getCommunityDisplay();

  const isImageFile = useMemo(() => {
    const validExtensions = [".jpg", ".jpeg", ".png", ".gif", ".webp", ".svg"];

    if (!fileUrl) return false;

    try {
      // Remove query params (?type=image/png etc.)
      const cleanUrl = fileUrl.split("?")[0];

      // Extract the file extension safely
      const match = cleanUrl.match(/\.[^.]+$/);
      const fileExtension = match ? match[0].toLowerCase() : "";

      console.log("File extension:", fileExtension);
      console.log("Is image file:", validExtensions.includes(fileExtension));

      return validExtensions.includes(fileExtension);
    } catch {
      return false;
    }
  }, [fileUrl]);



  const toggleDropdown = () => {
    setShowDropdown(!showDropdown);
  };

  const toggleModal = (value) => {
    setShowModal(value);
  };

  const toggleEditModal = (value) => {
    setShowEditModal(value);
  };

  const togglePublishModal = (value) => {
    setShowPublishModal(value);
  };

  const handleSavePost = async () => {
    if (isSaving || !post || !post._id) return;

    setIsSaving(true);
    try {
      if (isSaved) {
        await dispatch(unsavePostAction(post._id));
        dispatch(decreaseSavedByCount(post._id));
      } else {
        await dispatch(savePostAction(post._id));
        dispatch(increaseSavedByCount(post._id));
      }
      setIsSaved(!isSaved);
    } catch (error) {
      console.error("Error saving/unsaving post:", error);
    } finally {
      setIsSaving(false);
    }
  };

  const handleToggleComments = async (enableComments = false) => {
    if (!post || !post._id) return;

    try {
      const formData = new FormData();
      formData.append("allowComments", enableComments.toString());

      await dispatch(updatePostAction(post._id, formData));
    } catch (error) {
      console.error("Error toggling comments:", error);
    }
  };

  const handleMakePrivate = async () => {
    if (!post || !post._id) return;

    try {
      const formData = new FormData();
      formData.append("audience", "Private");
      formData.append("communities", JSON.stringify([]));

      await dispatch(updatePostAction(post._id, formData));
    } catch (error) {
      console.error("Error making post private:", error);
    }
  };

  // Load following users list on mount if not already loaded
  const hasFetchedFollowingRef = useRef(false);
  useEffect(() => {
    // If followingUsers is already populated, no need to fetch
    if (followingUsers && followingUsers.length > 0) {
      hasFetchedFollowingRef.current = false;
      return;
    }
    
    // Only fetch if user exists, followingUsers is empty, and we haven't already initiated a fetch
    if (userData?._id && !hasFetchedFollowingRef.current) {
      hasFetchedFollowingRef.current = true;
      dispatch(getFollowingUsersAction(100, 0));
    }
  }, [dispatch, userData?._id, followingUsers]);

  // Check if current user is following the post author
  const isFollowingAuthor = useMemo(() => {
    if (!user?._id || !followingUsers || followingUsers.length === 0) {
      return false;
    }
    return followingUsers.some(followingUser => followingUser._id === user._id);
  }, [user?._id, followingUsers]);

  // Check if current user has blocked the post author
  const isBlocked = useMemo(() => {
    if (!user?._id || !blockedUsers || blockedUsers.length === 0) {
      return false;
    }
    return blockedUsers.includes(user._id);
  }, [user?._id, blockedUsers]);

  const handleFollow = async () => {
    if (!user?._id || followLoading) return;
    
    setFollowLoading(true);
    try {
      await dispatch(followUserAndFetchData(user._id, userData));
      // Always refresh the following users list to update the UI
      await dispatch(getFollowingUsersAction(100, 0));
      setShowDropdown(false);
    } catch (error) {
      console.error("Error following user:", error);
    } finally {
      setFollowLoading(false);
    }
  };

  const handleUnfollow = async () => {
    if (!user?._id || unfollowLoading) return;
    
    setUnfollowLoading(true);
    try {
      const result = await dispatch(unfollowUserAction(user._id));
      if (result && result.success) {
        // Remove user from followingUsers list
        dispatch({
          type: types.REMOVE_FOLLOWING_USER,
          payload: user._id
        });
      }
      setShowDropdown(false);
    } catch (error) {
      console.error("Error unfollowing user:", error);
    } finally {
      setUnfollowLoading(false);
    }
  };

  const handleBlockUser = async () => {
    if (!user?._id || blockLoading) return;
    
    setBlockLoading(true);
    try {
      const result = await dispatch(blockUserAction(user._id));
      if (result && result.success) {
        setShowDropdown(false);
      }
    } catch (error) {
      console.error("Error blocking user:", error);
    } finally {
      setBlockLoading(false);
    }
  };

  const handleUnblockUser = async () => {
    if (!user?._id || blockLoading) return;
    
    setBlockLoading(true);
    try {
      const result = await dispatch(unblockUserAction(user._id));
      if (result && result.success) {
        setShowDropdown(false);
      }
    } catch (error) {
      console.error("Error unblocking user:", error);
    } finally {
      setBlockLoading(false);
    }
  };

  // Load saved posts and check if current post is saved (only when authenticated)
  useEffect(() => {
    if (userData?._id) {
      dispatch(getSavedPostsAction());
    }
  }, [dispatch, userData?._id]);

  useEffect(() => {
    if (post && post._id) {
      const savedPostsIds = savedPosts.map(savedPost => savedPost?._id);
      setIsSaved(savedPostsIds.includes(post._id));
    }
  }, [savedPosts, post]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setShowDropdown(false);
      }
      if (communityDropdownRef.current && !communityDropdownRef.current.contains(event.target)) {
        setShowCommunityDropdown(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);


  return (
    <div className="bg-white rounded-lg border pt-4 pr-6 pb-3 pl-3 md:pt-5 md:pr-8 md:pb-4 md:pl-4 shadow-sm w-full relative overflow-visible">
      {/* Save and 3-dots icons in top-right corner */}
      <div className="absolute top-4 right-3 flex items-center gap-1">
        <button
          onClick={handleSavePost}
          disabled={isSaving}
          className="text-gray-600 hover:text-gray-800 transition-colors flex items-center justify-center p-1"
        >
          {isSaved ? (
            <FaBookmark className={`w-4 h-4 text-primary-700`} />
          ) : (
            <FaRegBookmark className={`w-4 h-4 text-gray-400`} />
          )}
        </button>

        <div className="relative" ref={dropdownRef}>
          <button
            onClick={toggleDropdown}
            className="text-gray-400 hover:text-gray-600 transition-colors flex items-center justify-center p-1"
          >
            <HiOutlineEllipsisVertical className="w-5 h-5" />
          </button>

          {showDropdown && (
            <div className="absolute right-0 top-full mt-1 w-48 bg-white rounded-lg shadow-lg border border-gray-200 py-1 z-50 overflow-hidden">
              {/* Options for post owner */}
              {userData?._id === post?.user?._id && (
                <>
                  {/* Show Publish Post button only for private posts */}
                  {audience === "Private" && (
                    <button
                      onClick={() => {
                        togglePublishModal(true);
                        setShowDropdown(false);
                      }}
                      className="w-full flex items-center gap-3 px-4 py-2 text-sm text-primary-700 hover:bg-primary-50 font-flex-regular transition-colors"
                    >
                      <FaShare className="w-4 h-4 text-primary-700" />
                      <span className="font-flex-regular">Publish Post</span>
                    </button>
                  )}
                  
                  {allowComments && (
                    <button
                      onClick={() => {
                        handleToggleComments(false);
                        setShowDropdown(false);
                      }}
                      className="w-full flex items-center gap-3 px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 font-flex-regular transition-colors"
                    >
                      <HiOutlineChatBubbleOvalLeft className="w-4 h-4 text-gray-700" />
                      <span className="font-flex-regular">Turn off comments</span>
                    </button>
                  )}
                  
                  {!allowComments && (
                    <button
                      onClick={() => {
                        handleToggleComments(true);
                        setShowDropdown(false);
                      }}
                      className="w-full flex items-center gap-3 px-4 py-2 text-sm text-primary-700 hover:bg-primary-50 font-flex-regular transition-colors"
                    >
                      <HiOutlineChatBubbleOvalLeft className="w-4 h-4 text-primary-700" />
                      <span className="font-flex-regular">Turn on comments</span>
                    </button>
                  )}
                  
                  {audience !== "Private" && (
                    <button
                      onClick={() => {
                        handleMakePrivate();
                        setShowDropdown(false);
                      }}
                      className="w-full flex items-center gap-3 px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 font-flex-regular transition-colors"
                    >
                      <FaLock className="w-4 h-4 text-gray-700" />
                      <span className="font-flex-regular">Make Private Post</span>
                    </button>
                  )}
                  
                  <button
                    onClick={() => {
                      toggleModal(true);
                      setShowDropdown(false);
                    }}
                    className="w-full flex items-center gap-3 px-4 py-2 text-sm text-action hover:bg-action/10 font-flex-regular transition-colors"
                  >
                    <FaTrash className="w-4 h-4 text-action" />
                    <span className="font-flex-regular">Delete Post</span>
                  </button>
                </>
              )}

              {/* Options for other users' posts */}
              {userData?._id !== post?.user?._id && (
                <>
                  {isFollowingAuthor ? (
                    <button
                      onClick={handleUnfollow}
                      disabled={unfollowLoading}
                      className="w-full flex items-center gap-3 px-4 py-2 text-sm text-action hover:bg-action/10 font-flex-regular transition-colors disabled:opacity-50"
                    >
                      {unfollowLoading ? (
                        <span className="font-flex-regular">Unfollowing...</span>
                      ) : (
                        <>
                          <FaUserMinus className="w-4 h-4 text-action" />
                          <span className="font-flex-regular">Unfollow</span>
                        </>
                      )}
                    </button>
                  ) : (
                    <button
                      onClick={handleFollow}
                      disabled={followLoading}
                      className="w-full flex items-center gap-3 px-4 py-2 text-sm text-primary-700 hover:bg-primary-50 font-flex-regular transition-colors disabled:opacity-50"
                    >
                      {followLoading ? (
                        <span className="font-flex-regular">Following...</span>
                      ) : (
                        <>
                          <FaUserPlus className="w-4 h-4 text-primary-700" />
                          <span className="font-flex-regular">Follow</span>
                        </>
                      )}
                    </button>
                  )}
                  <button
                    onClick={() => {
                      setShowReportModal(true);
                      setShowDropdown(false);
                    }}
                    className="w-full flex items-center gap-3 px-4 py-2 text-sm text-action hover:bg-amber-50 font-flex-regular transition-colors"
                  >
                    <FaFlag className="w-4 h-4 text-action" />
                    <span className="font-flex-regular">Report Post</span>
                  </button>
                  {isBlocked ? (
                    <button
                      onClick={handleUnblockUser}
                      disabled={blockLoading}
                      className="w-full flex items-center gap-3 px-4 py-2 text-sm text-primary-700 hover:bg-primary-50 font-flex-regular transition-colors disabled:opacity-50"
                    >
                      {blockLoading ? (
                        <span className="font-flex-regular">Unblocking...</span>
                      ) : (
                        <>
                          <FaBan className="w-4 h-4 text-primary-700" />
                          <span className="font-flex-regular">Unblock User</span>
                        </>
                      )}
                    </button>
                  ) : (
                    <button
                      onClick={handleBlockUser}
                      disabled={blockLoading}
                      className="w-full flex items-center gap-3 px-4 py-2 text-sm text-action hover:bg-action/10 font-flex-regular transition-colors disabled:opacity-50"
                    >
                      {blockLoading ? (
                        <span className="font-flex-regular">Blocking...</span>
                      ) : (
                        <>
                          <FaBan className="w-4 h-4 text-action" />
                          <span className="font-flex-regular">Block User</span>
                        </>
                      )}
                    </button>
                  )}
                </>
              )}
            </div>
          )}
        </div>
      </div>
      {
        isSmallScreen ?
          <>
            <div className="flex items-start gap-3 mb-1 md:mb-2">
              <div className="flex-shrink-0">
                <img
                  className="rounded-full overflow-hidden w-12 h-12 object-cover"
                  src={user.avatar}
                  alt="user avatar"
                  loading="lazy"
                />
              </div>
              <div className="flex-1">
                <div className="flex flex-col">
                  <Link to={`/profile/${user?.profileUrl || user?._id}`} className="font-semibold text-sm md:text-base capitalize font-roboto-flex">
                    {user?.name}
                  </Link>
                  <div className="flex items-center gap-2">
                    {communityDisplay && (
                      communityDisplay.type === 'audience' ? (
                        <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-gradient-to-r from-primary-50 to-primary-100 text-primary-700 border border-primary-200 hover:from-primary-100 hover:to-primary-200 hover:border-primary-300 transition-all duration-200 font-roboto-flex shadow-sm">
                          {communityDisplay.name}
                        </span>
                      ) : (
                        <div className="flex items-center gap-1">
                          <Link
                            to={`/skills-feed/${communityDisplay.name}`}
                            className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-gradient-to-r from-primary-50 to-primary-100 text-primary-700 border border-primary-200 hover:from-primary-100 hover:to-primary-200 hover:border-primary-300 transition-all duration-200 font-roboto-flex shadow-sm"
                          >
                            {communityDisplay.name}
                          </Link>
                          {communityDisplay.type === 'multiple' && communityDisplay.remainingCount > 0 && (
                            <div className="relative" ref={communityDropdownRef}>
                              <button
                                onClick={() => setShowCommunityDropdown(!showCommunityDropdown)}
                                className="text-xs text-primary-700 hover:text-primary-800 cursor-pointer font-roboto-flex"
                              >
                                +{communityDisplay.remainingCount} more
                              </button>

                              <Transition
                                show={showCommunityDropdown}
                                enter="transition ease-out duration-100"
                                enterFrom="transform opacity-0 scale-95"
                                enterTo="transform opacity-100 scale-100"
                                leave="transition ease-in duration-75"
                                leaveFrom="transform opacity-100 scale-100"
                                leaveTo="transform opacity-0 scale-95"
                              >
                                <div className="absolute top-full left-0 mt-1 w-48 bg-white rounded-md shadow-lg border border-gray-200 z-50">
                                  <div className="py-1">
                                    {communityDisplay.allCommunities.slice(1).map((comm, index) => (
                                      <Link
                                        key={comm?._id}
                                        to={`/skills-feed/${comm.name}`}
                                        className="block px-3 py-2 text-xs font-roboto-flex hover:bg-gray-50"
                                        onClick={() => setShowCommunityDropdown(false)}
                                      >
                                        <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-gradient-to-r from-primary-50 to-primary-100 text-primary-700 border border-primary-200 hover:from-primary-100 hover:to-primary-200 hover:border-primary-300 transition-all duration-200 font-roboto-flex shadow-sm">
                                          {comm.name}
                                        </span>
                                      </Link>
                                    ))}
                                  </div>
                                </div>
                              </Transition>
                            </div>
                          )}
                        </div>
                      )
                    )}
                    <span className="text-gray-400 font-roboto-flex">|</span>
                    <p className="text-xs text-gray-500 font-roboto-flex">{createdAt}</p>
                  </div>
                </div>


              </div>
            </div>
            <div>
              {/* Post Content */}
              <div className="mt-4 mb-4">
                <div
                  onClick={() => {
                    navigate(`/post/${post?._id}`, {
                      state: { from: location.pathname },
                    });
                  }}
                  className="cursor-pointer break-words font-roboto-flex text-xs md:text-sm"
                >
                  <RichTextContent content={content} />
                </div>

                {/* Media Section */}
                <div className="mt-1">
                  {fileUrl && isImageFile ? (
                    <img
                      className="w-full max-w-3xl mx-auto rounded-md object-contain max-h-96"
                      src={fileUrl}
                      alt={`${fileType} file`}
                      loading="lazy"
                    />
                  ) : youtubeVideo ? (
                    <div className="w-full max-w-2xl mx-auto h-80">
                      <div className="relative w-full h-full rounded-md overflow-hidden bg-black">
                        <iframe
                          src={`https://www.youtube.com/embed/${youtubeVideo.videoId}?rel=0&modestbranding=1&enablejsapi=1`}
                          title={youtubeVideo.title}
                          className="w-full h-full border-0"
                          frameBorder="0"
                          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                          allowFullScreen
                          style={{ pointerEvents: 'auto' }}
                        />
                        <div className="absolute bottom-2 left-2 right-2 pointer-events-none">
                          <p className="text-white text-xs font-roboto-flex truncate bg-black bg-opacity-50 px-2 py-1 rounded">
                            {youtubeVideo.title}
                          </p>
                        </div>
                      </div>
                    </div>
                  ) : (
                    fileUrl && (
                      <video
                        className="w-full max-w-sm mx-auto rounded-md"
                        src={fileUrl}
                        controls
                      />
                    )
                  )}
                </div>
              </div>

              {/* Like/Comment Section */}
              <div className="flex items-center justify-between mt-3 pt-3 border-t border-gray-100">
                <div className="flex items-center gap-6">
                  <Like post={post} />

                  {allowComments && (
                    <button
                      className="flex items-center gap-2 text-gray-600 hover:text-gray-800 transition-colors font-roboto-flex"
                      onClick={() => {
                        navigate(`/post/${post?._id}`, {
                          state: { from: location.pathname },
                        });
                      }}
                    >
                      <HiOutlineChatBubbleOvalLeft className="w-5 h-5" />
                      <span className="text-sm font-roboto-flex">Comment</span>
                    </button>
                  )}

                </div>
              </div>

                    {/* Comments Section - Below Like/Comment bar with prominent grey backgrounds */}
                    {allowComments && comments && comments.length > 0 && (
                <div className="mt-3 space-y-2">
                  {/* Show all comments link with grey background */}
                  {comments.length > 1 && (
                    <div className="bg-gray-100 rounded-lg p-3 hover:bg-gray-200 transition-colors">
                      <button
                        onClick={() => {
                          navigate(`/post/${post?._id}`, {
                            state: { from: location.pathname },
                          });
                        }}
                        className="text-primary-600 hover:text-primary-700 text-sm font-semibold font-roboto-flex w-full text-left"
                      >
                        Show all comments ({comments.length})
                      </button>
                    </div>
                  )}

                  {/* Latest comment with grey background */}
                  <div className="bg-gray-100 rounded-lg p-3 hover:bg-gray-200 transition-colors">
                    <div className="flex items-start gap-2 md:gap-3">
                      <img
                        className="rounded-full w-10 h-10 md:w-12 md:h-12 object-cover flex-shrink-0"
                        src={comments[comments.length - 1]?.user?.avatar || placeholder}
                        alt="commenter avatar"
                        loading="lazy"

                      />
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-1">
                          <span className="text-sm md:text-base font-semibold text-gray-800 font-roboto-flex capitalize">
                            {comments[comments.length - 1]?.user?.name}
                          </span>
                          <span className="text-xs text-gray-500 font-roboto-flex">
                            {comments[comments.length - 1]?.createdAt}
                          </span>
                        </div>
                        <p className="text-xs md:text-sm text-gray-700 font-roboto-flex break-words">
                          {comments[comments.length - 1]?.content}
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </>
          :
          <div className="flex items-start gap-3 mb-1 md:mb-2">
            <div className="flex-shrink-0">
              <img
                className="rounded-full overflow-hidden w-12 h-12 object-cover"
                src={user.avatar}
                alt="user avatar"
                loading="lazy"
              />
            </div>
            <div className="flex-1">
              <div className="flex flex-col">
                <Link to={`/post/${user?.profileUrl || user?._id}`} className="font-semibold text-sm md:text-base capitalize font-roboto-flex">
                  {user?.name}
                </Link>
                <div className="flex items-center gap-2">
                  {communityDisplay && (
                    communityDisplay.type === 'audience' ? (
                      <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-gradient-to-r from-primary-50 to-primary-100 text-primary-700 border border-primary-200 hover:from-primary-100 hover:to-primary-200 hover:border-primary-300 transition-all duration-200 font-roboto-flex shadow-sm">
                        {communityDisplay.name}
                      </span>
                    ) : (
                      <div className="flex items-center gap-1">
                        <Link
                          to={`/skills-feed/${communityDisplay.name}`}
                          className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-gradient-to-r from-primary-50 to-primary-100 text-primary-700 border border-primary-200 hover:from-primary-100 hover:to-primary-200 hover:border-primary-300 transition-all duration-200 font-roboto-flex shadow-sm"
                        >
                          {communityDisplay.name}
                        </Link>
                        {communityDisplay.type === 'multiple' && communityDisplay.remainingCount > 0 && (
                          <div className="relative" ref={communityDropdownRef}>
                            <button
                              onClick={() => setShowCommunityDropdown(!showCommunityDropdown)}
                              className="text-xs text-primary-700 hover:text-primary-800 cursor-pointer font-roboto-flex"
                            >
                              +{communityDisplay.remainingCount} more
                            </button>

                            <Transition
                              show={showCommunityDropdown}
                              enter="transition ease-out duration-100"
                              enterFrom="transform opacity-0 scale-95"
                              enterTo="transform opacity-100 scale-100"
                              leave="transition ease-in duration-75"
                              leaveFrom="transform opacity-100 scale-100"
                              leaveTo="transform opacity-0 scale-95"
                            >
                              <div className="absolute top-full left-0 mt-1 w-48 bg-white rounded-md shadow-lg border border-gray-200 z-50">
                                <div className="py-1">
                                  {communityDisplay.allCommunities.slice(1).map((comm, index) => (
                                    <Link
                                      key={comm?._id}
                                      to={`/skills-feed/${comm.name}`}
                                      className="block px-3 py-2 text-xs font-roboto-flex hover:bg-gray-50"
                                      onClick={() => setShowCommunityDropdown(false)}
                                    >
                                      <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-gradient-to-r from-primary-50 to-primary-100 text-primary-700 border border-primary-200 hover:from-primary-100 hover:to-primary-200 hover:border-primary-300 transition-all duration-200 font-roboto-flex shadow-sm">
                                        {comm.name}
                                      </span>
                                    </Link>
                                  ))}
                                </div>
                              </div>
                            </Transition>
                          </div>
                        )}
                      </div>
                    )
                  )}
                  <span className="text-gray-400 font-roboto-flex">|</span>
                  <p className="text-xs text-gray-500 font-roboto-flex">{createdAt}</p>
                </div>
              </div>
              {/* Post Content */}
              <div className="mt-4 mb-4">
                <div
                  onClick={() => {
                    navigate(`/post/${post?._id}`, {
                      state: { from: location.pathname },
                    });
                  }}
                  className="cursor-pointer break-words font-roboto-flex text-xs md:text-sm"
                >
                  <RichTextContent content={content} />
                </div>

                {/* Media Section */}
                <div className="mt-1">
                  {fileUrl && isImageFile ? (
                    <img
                      className="w-full max-w-3xl mx-auto rounded-md object-contain max-h-96"
                      src={fileUrl}
                      alt={`${fileType} file`}
                      loading="lazy"
                      onError={(e) => {
                        e.target.onerror = null;
                        e.target.src = placeholder;
                      }}
                    />
                  ) : youtubeVideo ? (
                    <div className="w-full max-w-2xl mx-auto h-80">
                      <div className="relative w-full h-full rounded-md overflow-hidden bg-black">
                        <iframe
                          src={`https://www.youtube.com/embed/${youtubeVideo.videoId}?rel=0&modestbranding=1&enablejsapi=1`}
                          title={youtubeVideo.title}
                          className="w-full h-full border-0"
                          frameBorder="0"
                          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                          allowFullScreen
                          style={{ pointerEvents: 'auto' }}
                        />
                        <div className="absolute bottom-2 left-2 right-2 pointer-events-none">
                          <p className="text-white text-xs font-roboto-flex truncate bg-black bg-opacity-50 px-2 py-1 rounded">
                            {youtubeVideo.title}
                          </p>
                        </div>
                      </div>
                    </div>
                  ) : (
                    fileUrl && (
                      <video
                        className="w-full max-w-sm mx-auto rounded-md"
                        src={fileUrl}
                        controls
                      />
                    )
                  )}
                </div>
              </div>

              {/* Like/Comment Section */}
              <div className="flex items-center justify-between mt-3 pt-3 border-t border-gray-100">
                <div className="flex items-center gap-6">
                  <Like post={post} />

                  {allowComments && (
                    <button
                      className="flex items-center gap-2 text-gray-600 hover:text-gray-800 transition-colors font-roboto-flex"
                      onClick={() => {
                        navigate(`/post/${post?._id}`, {
                          state: { from: location.pathname },
                        });
                      }}
                    >
                      <HiOutlineChatBubbleOvalLeft className="w-5 h-5" />
                      <span className="text-sm font-roboto-flex">Comment</span>
                    </button>
                  )}

                </div>
              </div>

              {/* Comments Section - Below Like/Comment bar with prominent grey backgrounds */}
              {allowComments && comments && comments.length > 0 && (
                <div className="mt-3 space-y-2">
                  {/* Show all comments link with grey background */}
                  {comments.length > 1 && (
                    <div className="bg-gray-100 rounded-lg p-3 hover:bg-gray-200 transition-colors">
                      <button
                        onClick={() => {
                          navigate(`/post/${post?._id}`, {
                            state: { from: location.pathname },
                          });
                        }}
                        className="text-primary-600 hover:text-primary-700 text-sm font-semibold font-roboto-flex w-full text-left"
                      >
                        Show all comments ({comments.length})
                      </button>
                    </div>
                  )}

                  {/* Latest comment with grey background */}
                  <div className="bg-gray-100 rounded-lg p-3 hover:bg-gray-200 transition-colors">
                    <div className="flex items-start gap-2 md:gap-3">
                      <img
                        className="rounded-full w-10 h-10 md:w-12 md:h-12 object-cover flex-shrink-0"
                        src={comments[comments.length - 1]?.user?.avatar || placeholder}
                        alt="commenter avatar"
                        loading="lazy"

                      />
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-1">
                          <span className="text-sm md:text-base font-semibold text-gray-800 font-roboto-flex capitalize">
                            {comments[comments.length - 1]?.user?.name}
                          </span>
                          <span className="text-xs text-gray-500 font-roboto-flex">
                            {comments[comments.length - 1]?.createdAt}
                          </span>
                        </div>
                        <p className="text-xs md:text-sm text-gray-700 font-roboto-flex break-words">
                          {comments[comments.length - 1]?.content}
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              )}

            </div>
          </div>


      }



      {/* Delete Modal */}
      <DeleteModal
        showModal={showModal}
        postId={post?._id}
        onClose={() => toggleModal(false)}
        prevPath={location.pathname}
      />

      {/* Edit Modal */}
      <EditPostModal
        showModal={showEditModal}
        post={post}
        onClose={() => toggleEditModal(false)}
      />

      {/* Publish Modal */}
      <PublishPostModal
        showModal={showPublishModal}
        post={post}
        onClose={() => togglePublishModal(false)}
      />

      {/* Report Modal */}
      {showReportModal && (
        <ReportPostModal
          isOpen={showReportModal}
          onClose={() => setShowReportModal(false)}
          postId={post?._id}
        />
      )}
    </div>
  );
};

// Add conditional render wrapper
const SavedPostWrapper = ({ post }) => {
  if (!post || typeof post !== 'object') {
    console.error('Post data is invalid:', post);
    return null;
  }
  return <SavedPost post={post} />;
};

export default SavedPostWrapper;
